import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { importsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Upload, ArrowLeft, Loader2, FileSpreadsheet, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CSV_TEMPLATE = `name,sku,unit_cost,billable_price,category
Example Air Filter 16x20,AF-1620,12.00,29.99,Filters
Example PVC Pipe 1/2 inch,PVC-050,3.50,8.99,Supplies
Example Copper Elbow 90deg,CU-90E,4.25,11.50,Plumbing`;

export const InventoryImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'catalogue_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await importsAPI.upload('inventory_items', file);
      const sessionId = res.data.id;
      toast.success(`Uploaded ${res.data.total_rows} rows — reviewing results`);
      navigate(`/import-review/${sessionId}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to import CSV';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="inventory-import-page">
        <Button variant="ghost" onClick={() => navigate('/catalogue')} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parts List
        </Button>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6" />
              Import Parts List from CSV
            </CardTitle>
            <CardDescription>
              Add items to your master catalogue. Failed rows can be corrected and re-imported on the review page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Import Error</p>
                    <p className="text-sm text-destructive/80 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div
              data-testid="csv-upload-dropzone"
              className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => !loading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
                disabled={loading}
                data-testid="file-input"
              />
              {loading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                  <p className="text-lg font-semibold">Uploading & processing...</p>
                  <p className="text-sm text-muted-foreground mt-1">This may take a moment for large files</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">Click to upload CSV file</p>
                  <p className="text-sm text-muted-foreground mt-2">You&apos;ll be taken to the review page to see results</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-semibold mb-2">Expected CSV Columns:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li><span className="font-medium text-foreground">Required:</span> name (or Item Name)</li>
                <li><span className="font-medium">Recommended:</span> sku</li>
                <li><span className="font-medium">Optional:</span> quantity, unit_cost, billable_price, category</li>
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                Note: If quantity is included, matching SKUs will have their stock increased.
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template CSV
              </Button>
            </div>

            <Button variant="outline" className="w-full" onClick={() => navigate('/import-history')} data-testid="view-import-history-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              View Import History
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default InventoryImport;
